import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));


  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const adminId = localStorage.getItem('adminId');
      
      if (storedToken && adminId) {
        try {
          // Validate session first
          const sessionRes = await fetch(
            `${process.env.REACT_APP_API_URL}api/admin/validate-session`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${storedToken}`,
              },
            }
          );

          if (sessionRes.ok) {
            setToken(storedToken);
            // Fetch user data
            await fetchUserData(adminId, storedToken);
          } else {
            localStorage.clear();
            setUser(null);
            setToken(null);
          }
        } catch (error) {
          console.error("Auth initialization failed:", error);
          localStorage.clear();
          setUser(null);
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Add periodic session validation
  useEffect(() => {
    if (!token || !user) return;

    const validateSessionPeriodically = async () => {
      try {
        const sessionRes = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/validate-session`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!sessionRes.ok) {
          // Session expired, logout user
          logout();
          toast.error('Your session has expired. Please log in again.');
        }
      } catch (error) {
        console.error("Session validation failed:", error);
        logout();
        toast.error('Session validation failed. Please log in again.');
      }
    };

    // Validate session every 5 minutes
    const interval = setInterval(validateSessionPeriodically, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token, user]);

  const fetchUserData = async (adminId, authToken = token) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/admin/id/${adminId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const json = await response.json();

      if (response.ok) {
        setUser(json.data);
        return json.data;
      } else if (response.status === 401) {
        logout();
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  const login = async (email, password) => {
    const values = { email, password };
    const loadingToast = toast.loading("Signing in...");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/admin/login`,
        {
          method: "POST",
          body: JSON.stringify(values),
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const json = await response.json();

      if (response.ok) {
        const { accessToken, admin } = json.data;
        
        localStorage.setItem("token", accessToken);
        localStorage.setItem("adminId", admin.id);
        
        setToken(accessToken);
        setUser(admin);

        toast.dismiss(loadingToast);
        toast.success("Signed in successfully!");
        
        return { success: true };
      } else {
        toast.dismiss(loadingToast);
        toast.error(json.errorMessage);
        return { success: false, error: json.errorMessage };
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    const loadingToast = toast.loading("Signing out...");

    try {
      if (token) {
        await fetch(`${process.env.REACT_APP_API_URL}api/admin/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      localStorage.clear();
      setUser(null);
      setToken(null);
      toast.dismiss(loadingToast);
      toast.success("Logged out successfully!");
    }
  };

  const updateUser = (updatedUser) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUser }));
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    fetchUserData,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};